import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { X } from 'phosphor-react';
import { theme } from '../../styles/theme';
import { useTranslation } from '../../i18n';

interface MentalHealthIntroModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  z-index: ${theme.zIndex.modal + 1};
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: flex-end;
  animation: ${fadeIn} 0.3s ease-out;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
  background: var(--color-bg);
  border-radius: 24px 24px 0 0;
  animation: ${slideUp} 0.3s ease-out;
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
  outline: none;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb {
    background: var(--color-surface-dark);
    border-radius: ${theme.borderRadius.sm};
  }
`;

const HeaderImage = styled.img`
  display: block;
  width: 100%;
  height: auto;
  border-radius: 24px 24px 0 0;
  object-fit: cover;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  border: none;
  border-radius: ${theme.borderRadius.full};
  color: #ffffff;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  backdrop-filter: blur(8px);

  &:hover { background: rgba(0, 0, 0, 0.6); }
  &:active { transform: scale(0.95); }
`;

const Body = styled.div`
  padding: 20px 24px 8px;
`;

const Title = styled.h2`
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 16px 0;
  line-height: 1.35;
`;

const Paragraph = styled.p`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 13px;
  color: var(--color-text);
  line-height: 1.6;
  margin: 0 0 14px 0;

  strong {
    font-weight: 700;
    color: var(--color-primary);
  }
`;

const Disclaimer = styled.p`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: 12px;
  color: var(--color-text-secondary);
  line-height: 1.55;
  margin: 18px 0 0 0;
  padding: 14px 16px;
  background: var(--color-bg-elevated);
  border-radius: 12px;
`;

const Footer = styled.div`
  flex-shrink: 0;
  padding: 12px 24px max(20px, env(safe-area-inset-bottom));
  background: var(--color-bg);
  border-top: 1px solid var(--color-border-subtle);
  box-shadow: 0 -8px 16px -8px rgba(0, 0, 0, 0.18);
`;

const CtaButton = styled.button`
  width: 100%;
  padding: 16px;
  background: var(--color-primary);
  border: none;
  border-radius: 14px;
  font-family: ${theme.typography.fontFamily.primary};
  font-size: 15px;
  font-weight: 600;
  color: #ffffff;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover { opacity: 0.9; transform: translateY(-2px); }
  &:active { transform: translateY(0); }
`;

export const MentalHealthIntroModal: React.FC<MentalHealthIntroModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Prevent body scroll while open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on escape (listener only attached while open)
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Move focus into the dialog on open, restore it on close/unmount
  useEffect(() => {
    if (!isOpen) return;
    previousFocusRef.current = document.activeElement as HTMLElement | null;
    modalRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, [isOpen]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  if (!isOpen) return null;

  return (
    <Overlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <Modal
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('mentalHealthIntro.title')}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton onClick={onClose} aria-label="Close">
          <X size={18} weight="bold" />
        </CloseButton>

        <ScrollArea>
          <HeaderImage src="/img/soul-mental-health-top.webp" alt="" />
          <Body>
            <Title>{t('mentalHealthIntro.title')}</Title>
            <Paragraph>{t('mentalHealthIntro.intro')}</Paragraph>
            <Paragraph>
              <strong>{t('mentalHealthIntro.reflectiveLabel')}</strong>{' '}
              {t('mentalHealthIntro.reflectiveBody')}
            </Paragraph>
            <Paragraph>
              <strong>{t('mentalHealthIntro.solutionLabel')}</strong>{' '}
              {t('mentalHealthIntro.solutionBody')}
            </Paragraph>
            <Paragraph>{t('mentalHealthIntro.closing')}</Paragraph>
            <Disclaimer>{t('mentalHealthIntro.disclaimer')}</Disclaimer>
          </Body>
        </ScrollArea>

        <Footer>
          <CtaButton onClick={onClose}>{t('mentalHealthIntro.cta')}</CtaButton>
        </Footer>
      </Modal>
    </Overlay>
  );
};
